import {ZadProduct, ZadProductPackaging, ZadPurchaseUnit, ZadSaleUnit} from '../../../models/zad'

export const ZAD_PACKAGE_UNIT_OPTIONS = ['BOX', 'PACK', 'LOT', 'CARTON', 'PACKAGE'] as const

export function isZadPackagingEnabled(product?: {packaging?: ZadProductPackaging | null} | null): boolean {
  return product?.packaging?.enabled === true
}

export function zadUnitsPerPackage(product?: {packaging?: ZadProductPackaging | null} | null): number {
  if (!isZadPackagingEnabled(product)) return 1
  const n = Number(product?.packaging?.unitsPerPackage)
  return Number.isInteger(n) && n > 1 ? n : 1
}

export function zadAllowedSaleUnits(product?: ZadProduct | null): ZadSaleUnit[] {
  if (!isZadPackagingEnabled(product)) return ['UNIT']
  const units: ZadSaleUnit[] = []
  if (product?.packaging?.allowUnitSale !== false) units.push('UNIT')
  if (product?.packaging?.allowPackageSale === true) units.push('PACKAGE')
  return units.length ? units : ['UNIT']
}

export function normalizeZadSaleUnit(raw?: string | null): ZadSaleUnit {
  return String(raw || 'UNIT').trim().toUpperCase() === 'PACKAGE' ? 'PACKAGE' : 'UNIT'
}

export function normalizeZadPurchaseUnit(raw?: string | null): ZadPurchaseUnit {
  return String(raw || 'UNIT').trim().toUpperCase() === 'PACKAGE' ? 'PACKAGE' : 'UNIT'
}

export function selectedZadSaleUnitFromBarcode(raw?: string | null): ZadSaleUnit {
  return normalizeZadSaleUnit(raw)
}

export function resolveZadUnitSalePrice(product?: ZadProduct | null): number | null {
  if (!product) return null
  if (isZadPackagingEnabled(product) && product.packaging?.unitSalePrice != null && product.packaging.unitSalePrice !== ('' as any)) {
    const parsed = Number(product.packaging.unitSalePrice)
    if (Number.isFinite(parsed)) return parsed
  }
  const sale = Number(product.salePrice)
  return Number.isFinite(sale) ? sale : null
}

export function resolveZadPackageSalePrice(product?: ZadProduct | null): number | null {
  if (!isZadPackagingEnabled(product)) return null
  const parsed = Number(product?.packaging?.packageSalePrice)
  return Number.isFinite(parsed) ? parsed : null
}

export function resolveZadSaleUnitPrice(product: ZadProduct | null | undefined, saleUnit: ZadSaleUnit): number | null {
  if (normalizeZadSaleUnit(saleUnit) === 'PACKAGE') return resolveZadPackageSalePrice(product)
  return resolveZadUnitSalePrice(product)
}

export function defaultZadSaleUnit(product?: ZadProduct | null, preferred?: string | null): ZadSaleUnit {
  const allowed = zadAllowedSaleUnits(product)
  if (preferred) {
    const unit = normalizeZadSaleUnit(preferred)
    if (allowed.includes(unit)) return unit
  }
  return allowed[0] || 'UNIT'
}

export function toZadBaseUnits(
  quantity: number,
  unit?: string | null,
  unitsPerPackage?: number | null
): number {
  if (!Number.isFinite(quantity) || quantity <= 0) return 0
  if (normalizeZadSaleUnit(unit) !== 'PACKAGE') return quantity
  const upp = Number(unitsPerPackage)
  if (!Number.isInteger(upp) || upp < 2) return quantity
  return quantity * upp
}

export function zadPackagingLabel(
  productOrLabel: {packaging?: ZadProductPackaging | null} | string | null | undefined,
  saleUnit?: string | null,
  unitsPerPackage?: number | null
): string {
  if (typeof productOrLabel === 'string' && productOrLabel.trim()) return productOrLabel.trim()
  const unit = normalizeZadSaleUnit(saleUnit)
  if (unit !== 'PACKAGE') return 'UNIT'
  const pack =
    productOrLabel && typeof productOrLabel === 'object'
      ? productOrLabel.packaging?.packageUnit || 'PACKAGE'
      : 'PACKAGE'
  const upp = unitsPerPackage || zadUnitsPerPackage(typeof productOrLabel === 'object' ? productOrLabel : null)
  return `${pack} de ${upp}`
}

export function formatZadPackagingLabelI18n(
  label: string | null | undefined,
  saleUnit: string | null | undefined,
  formatMessage: (id: string, values?: Record<string, string | number>) => string
): string {
  const stored = String(label || '').trim()
  if (stored && stored !== 'UNIT' && stored !== 'Unité') return stored
  if (normalizeZadSaleUnit(saleUnit) === 'PACKAGE' && stored) return stored
  if (normalizeZadSaleUnit(saleUnit) === 'PACKAGE') {
    return formatMessage('ZAD.PACKAGING.SALE_PACKAGE')
  }
  return formatMessage('ZAD.PACKAGING.SALE_UNIT')
}

export function formatZadEquivalentStock(
  baseUnits: number,
  unitsPerPackage: number,
  packageUnit = 'PACKAGE'
): {packages: number; remainder: number; packageUnit: string} | null {
  if (!Number.isFinite(baseUnits) || !Number.isInteger(unitsPerPackage) || unitsPerPackage < 2) return null
  const packages = Math.floor(Math.max(0, baseUnits) / unitsPerPackage)
  const remainder = Math.max(0, baseUnits) % unitsPerPackage
  return {packages, remainder, packageUnit}
}

export function saleLineKey(productId: string, saleUnit?: string | null): string {
  return `${productId}:${normalizeZadSaleUnit(saleUnit)}`
}

export function zadSaleLinePackagingSubtitle(item: {
  saleUnit?: string | null
  packagingLabelSnapshot?: string | null
  packagingEnabled?: boolean
}): string | null {
  const unit = normalizeZadSaleUnit(item.saleUnit)
  const snapshot = String(item.packagingLabelSnapshot || '').trim()
  if (!item.packagingEnabled && unit !== 'PACKAGE' && !snapshot) return null
  if (snapshot && snapshot !== 'UNIT') return snapshot
  return unit
}

export function zadPurchaseReceivedBaseUnits(
  quantity: number | string,
  purchaseUnit?: string | null,
  unitsPerPackage?: number | null
): number {
  const qty = Number(quantity)
  if (!Number.isFinite(qty) || qty <= 0) return 0
  return toZadBaseUnits(qty, purchaseUnit, unitsPerPackage)
}

export function toZadPackagingWritePayload(values: {
  packagingEnabled?: boolean
  packagingWasEnabled?: boolean
  packaging?: ZadProductPackaging | null
  packagingBaseUnit?: string
  packagingPackageUnit?: string
  packagingUnitsPerPackage?: string | number
  packagingAllowUnitSale?: boolean
  packagingAllowPackageSale?: boolean
  packagingPackageSalePrice?: string | number
  packagingUnitBarcode?: string
  packagingPackageBarcode?: string
  salePrice?: string | number
  barcode?: string
}): ZadProductPackaging | undefined {
  if (values.packaging && typeof values.packaging.enabled === 'boolean' && values.packagingEnabled == null) {
    return values.packaging.enabled ? values.packaging : {enabled: false}
  }
  if (values.packagingEnabled !== true) {
    if (values.packagingWasEnabled || values.packaging?.enabled === false) return {enabled: false}
    return undefined
  }
  const units = Number(values.packagingUnitsPerPackage)
  const packagePriceRaw = String(values.packagingPackageSalePrice ?? '').trim().replace(',', '.')
  const packagePrice = packagePriceRaw === '' ? null : Number(packagePriceRaw)
  return {
    enabled: true,
    baseUnit: String(values.packagingBaseUnit || 'PIECE').trim() || 'PIECE',
    packageUnit: String(values.packagingPackageUnit || 'BOX').trim() || 'BOX',
    unitsPerPackage: Number.isInteger(units) ? units : 0,
    allowUnitSale: values.packagingAllowUnitSale !== false,
    allowPackageSale: values.packagingAllowPackageSale !== false,
    unitSalePrice: null,
    packageSalePrice: packagePrice != null && Number.isFinite(packagePrice) ? packagePrice : null,
    unitBarcode: String(values.packagingUnitBarcode || '').trim() || null,
    packageBarcode: String(values.packagingPackageBarcode || '').trim() || null,
  }
}
